import { IWeeklyChartDataItem } from "../../types"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Text,
  ReferenceLine,
} from "recharts"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"
import dayjs from "dayjs"
import { US_DATE_FORMAT_WITH_YEAR } from "config"
import ChartXAxisTick from "../ChartXAxisTick"

function GoalLabel(props: any) {
  const { viewBox } = props

  const labelRef = useRef<any>(null)
  const [shouldRender, setShouldRender] = useState(false)
  const [labelWidth, setLabelWidth] = useState(0)

  const HORIZONTAL_PADDING = 6
  const VERTICAL_PADDING = 2

  useLayoutEffect(() => {
    if (labelRef.current) {
      const width = labelRef.current.getBBox().width
      setLabelWidth(width)
      setShouldRender(true)
    }
  }, [])

  const rectWidth = labelWidth + HORIZONTAL_PADDING * 2
  const rectX = viewBox.x + viewBox.width - rectWidth - 20
  const y = viewBox.y

  return (
    <g style={{ opacity: shouldRender ? 1 : 0 }}>
      <rect
        fill="#4BD9DE"
        stroke="rgba(255, 255, 255, 0.2)"
        strokeWidth={1}
        rx={10}
        ry={10}
        x={rectX}
        y={y - 10}
        width={rectWidth}
        height={20}
      />
      <text
        ref={labelRef}
        x={rectX + HORIZONTAL_PADDING}
        y={y + VERTICAL_PADDING + 1}
        fill="#00263A"
        fontSize={12}
        fontWeight={600}
        letterSpacing={0.24}
      >
        Goal
      </text>
    </g>
  )
}

const XAXIS_HEIGHT = 18
const TICK_FONT_SIZE = 10
const TOP_TICK_OFFSET = TICK_FONT_SIZE / 2

const BAR_GAP_PX = 1
const BAR_RADIUS = 2

const CustomBarShape = (props: any) => {
  const { x, y, width, height, fill, dimmed, isTopSegment, isBottomSegment } =
    props
  if (!height || height <= 0) return null

  // Always create gap at the top of each segment, except the topmost one
  const adjustedY = isTopSegment ? y : y + BAR_GAP_PX
  const adjustedHeight = isTopSegment
    ? height
    : Math.max(height - BAR_GAP_PX, 0)

  const opacity = dimmed ? 0.2 : 1

  if (isBottomSegment) {
    const r = Math.min(BAR_RADIUS, adjustedHeight / 2, width / 2)
    const d = `M${x},${adjustedY + r}A${r},${r},0,0,1,${x + r},${adjustedY}H${
      x + width - r
    }A${r},${r},0,0,1,${x + width},${adjustedY + r}V${
      adjustedY + adjustedHeight
    }H${x}Z`
    return <path d={d} fill={fill} opacity={opacity} />
  }

  return (
    <rect
      x={x}
      y={adjustedY}
      width={width}
      height={adjustedHeight}
      fill={fill}
      rx={BAR_RADIUS}
      ry={BAR_RADIUS}
      opacity={opacity}
    />
  )
}

// Stack order bottom→top: other, snack, dinner, lunch, breakfast
const STACK_ORDER = ["breakfast", "lunch", "dinner", "snack", "other"] as const

const getTopSegmentKey = (item: IWeeklyChartDataItem) => {
  for (const key of STACK_ORDER) {
    if (item[key] > 0) return key
  }
  return "other"
}

const BOTTOM_STACK_ORDER = [
  "other",
  "snack",
  "dinner",
  "lunch",
  "breakfast",
] as const

const getBottomSegmentKey = (item: IWeeklyChartDataItem) => {
  for (const key of BOTTOM_STACK_ORDER) {
    if (item[key] > 0) return key
  }
  return "other"
}

const roundUp = (caloriesPerDay: number) => {
  const step = 500
  for (let index = 1; index < 1000; index++) {
    if (caloriesPerDay < step * index) {
      return step * index
    }
  }

  return 0
}

const roundDown = (caloriesPerDay: number) => {
  const step = 500
  for (let index = 1; index < 100; index++) {
    if (caloriesPerDay < step * index) {
      return step * (index - 1)
    }
  }

  return 0
}

const getMaxCaloriesPerDay = (data: IWeeklyChartDataItem[]) => {
  let max = 0
  data.forEach((item) => {
    const total =
      item.breakfast + item.lunch + item.dinner + item.snack + item.other
    if (total > max) {
      max = total
    }
  })
  return max
}

const getMinCaloriesPerDay = (data: IWeeklyChartDataItem[]) => {
  let min = 1000000
  data.forEach((item) => {
    const total =
      item.breakfast + item.lunch + item.dinner + item.snack + item.other
    if (total < min) {
      min = total
    }
  })
  return min
}

export const getYDomain = (
  data: IWeeklyChartDataItem[],
  goal: number,
  averageGainedCaloriesPerDay: number,
) => {
  const max =
    averageGainedCaloriesPerDay > goal ? averageGainedCaloriesPerDay : goal
  const min =
    averageGainedCaloriesPerDay > goal ? goal : averageGainedCaloriesPerDay

  const maxPerDay = getMaxCaloriesPerDay(data)
  const minPerDay = getMinCaloriesPerDay(data)

  const maxYValue = max > maxPerDay ? max : maxPerDay
  const maxYRounded = roundUp(maxYValue)

  const minYValue = min > minPerDay ? minPerDay : min
  const minYRounded = roundDown(minYValue)

  return [minYRounded, maxYRounded]
}

const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  .recharts-reference-line {
    clip-path: inset(0 1px);
  }
`

const WeeklyBarChart = ({
  data,
  averageGainedCaloriesPerDay,
  activeIndex,
  onBarClick,
}: {
  data: IWeeklyChartDataItem[]
  averageGainedCaloriesPerDay: number
  activeIndex: number | null
  onBarClick: (index: number | null) => void
}) => {
  const goalCalories = data[data.length - 1].goalCalories

  const hasData = data.some(
    (item) =>
      item.breakfast > 0 ||
      item.lunch > 0 ||
      item.dinner > 0 ||
      item.snack > 0 ||
      item.other > 0,
  )

  const [, computedMaxYAxis] = getYDomain(
    data,
    goalCalories,
    averageGainedCaloriesPerDay,
  )

  const maxYAxis =
    !hasData && goalCalories > 0
      ? roundUp(goalCalories * 2)
      : computedMaxYAxis

  const handleChartClick = useCallback(
    (state: any) => {
      if (state?.activeTooltipIndex !== undefined) {
        onBarClick(state.activeTooltipIndex as number)
      } else {
        onBarClick(null)
      }
    },
    [onBarClick],
  )

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          width={500}
          height={300}
          data={data}
          margin={{
            top: 6,
            bottom: 0,
          }}
          barSize={34}
          onClick={handleChartClick}
        >
          <XAxis
            interval={0}
            height={XAXIS_HEIGHT}
            tickLine={false}
            scale="point"
            dataKey="name"
            padding={{ left: 16, right: 16 }}
            tick={(tickObject) => {
              const dimmed =
                activeIndex !== null && tickObject.index !== activeIndex
              const isToday =
                data[tickObject.index].date ===
                dayjs().format(US_DATE_FORMAT_WITH_YEAR)
              return (
                <ChartXAxisTick
                  tickProps={tickObject}
                  isToday={isToday}
                  dimmed={dimmed}
                />
              )
            }}
          />

          <YAxis
            orientation="right"
            domain={[0, maxYAxis]}
            tickMargin={0}
            axisLine={false}
            width={40}
            stroke=""
            tick={(tickObject) => {
              const isTopTick = tickObject.index === (tickObject.visibleTicksCount ?? 0) - 1
              return (
                <Text {...tickObject} fill="#ffffffcc" width={26} fontSize={TICK_FONT_SIZE}
                  y={isTopTick ? tickObject.y - TOP_TICK_OFFSET : tickObject.y}>
                  {tickObject.payload.value}
                </Text>
              )
            }}
          />

          <CartesianGrid
            vertical={false}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={0.7}
          />

          {(
            [
              { key: "other", color: "#667085" },
              { key: "snack", color: "#F7BA1E" },
              { key: "dinner", color: "#8948FA" },
              { key: "lunch", color: "#FF7A1A" },
              { key: "breakfast", color: "#2D96FF" },
            ] as const
          ).map(({ key, color }) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={color}
              shape={(props: any) => {
                const item = data[props.index]
                return (
                  <CustomBarShape
                    {...props}
                    dimmed={activeIndex !== null && props.index !== activeIndex}
                    isTopSegment={key === getTopSegmentKey(item)}
                    isBottomSegment={key === getBottomSegmentKey(item)}
                  />
                )
              }}
            />
          ))}

          {goalCalories > 0 && (
            <ReferenceLine
              y={goalCalories}
              label={<GoalLabel />}
              stroke="#4BD9DE"
              strokeDasharray="3 4"
              strokeLinecap="round"
              strokeDashoffset={0}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

export default WeeklyBarChart
